// The worked example's page text, loaded only when something asks to read it.
//
// A review the user opened themselves keeps its page text in IndexedDB, put
// there by ingestion. The example never goes through ingestion, so its pages
// are extracted ahead of time by tools/seed-pages.ts — from the same PDF that
// ships in public/samples, through the same layout code the browser ingest uses
// — and committed as data.
//
// Seventy kilobytes of plan text should not be in the bundle every visitor
// downloads, so the import is dynamic: the chunk is fetched the first time the
// document pane opens on the example and never again. Everything else about the
// example — the findings, their spans, the whole workspace — renders without
// it.

import type { ExtractedPages } from '../../types';

/** The shape tools/seed-pages.ts writes. */
interface SeedPagesFile {
  docId: string;
  source: string;
  pageCount: number;
  pages: { number: number; text: string }[];
}

/** Document ids whose page text is shipped rather than stored. */
const SEEDED_PAGE_IDS = ['piscataway'] as const;

export function hasSeededPages(docId: string | undefined): boolean {
  return docId !== undefined && (SEEDED_PAGE_IDS as readonly string[]).includes(docId);
}

let cached: Promise<ExtractedPages> | null = null;

/**
 * The example's pages, or undefined for any other document.
 *
 * The promise is kept rather than the result, so two panes asking at once share
 * one fetch. A failed load is not cached: the chunk request can fail on a bad
 * connection, and a reviewer who reopens the pane should get another attempt
 * rather than a permanent "no document".
 */
export async function getSeededPages(
  docId: string | undefined,
): Promise<ExtractedPages | undefined> {
  if (!hasSeededPages(docId)) return undefined;
  if (!cached) {
    cached = import('./piscataway-pages.json')
      .then((module) => toExtractedPages(module.default as SeedPagesFile))
      .catch((error) => {
        cached = null;
        throw error;
      });
  }
  return cached;
}

function toExtractedPages(file: SeedPagesFile): ExtractedPages {
  return {
    docId: file.docId,
    pageCount: file.pageCount,
    pages: file.pages,
    // The example's text was extracted when the tool last ran, not when this
    // browser opened it. Naming the source rather than inventing a timestamp.
    extractedAt: '',
  };
}
